import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { PageShell } from '@/components/layout/page-shell';
import { updateSettings } from '@/api/me';
import { haptic } from '@/lib/telegram';
import { useAuth } from '@/hooks/use-auth';

const commonTimezones = [
  'UTC',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Moscow',
  'Asia/Tbilisi',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Tokyo',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
];

export default function ProfilePage() {
  const auth = useAuth();

  const [timezone, setTimezone] = useState('UTC');
  const [notificationTime, setNotificationTime] = useState('09:00');
  const [reminderInterval, setReminderInterval] = useState(60);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (auth.user) {
      setTimezone(auth.user.timezone || 'UTC');
    }
  }, [auth.user]);

  const save = async () => {
    setSaving(true);
    haptic('light');
    try {
      await updateSettings({
        timezone,
        notification_time: notificationTime,
        reminder_interval: reminderInterval,
        is_notifications_enabled: notificationsEnabled,
      });
      haptic('success');
      toast.success('Saved');
    } catch {
      haptic('error');
      toast.error('Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageShell title="Profile">
      <div className="flex flex-col gap-4 px-4 py-4">
        <div className="rounded-xl bg-[var(--color-secondary-bg)] px-4 py-4">
          <h2 className="text-lg font-semibold">
            {auth.user?.first_name} {auth.user?.last_name}
          </h2>
          {auth.user?.username ? (
            <p className="text-sm text-[var(--color-muted)]">@{auth.user.username}</p>
          ) : null}
          {auth.user?.subscription_tier ? (
            <p className="mt-2 inline-block rounded-full bg-[var(--color-button)]/15 px-3 py-0.5 text-xs text-[var(--color-accent)]">
              {auth.user.subscription_tier}
            </p>
          ) : null}
        </div>

        <label className="flex flex-col gap-1">
          <span className="text-sm font-medium">Timezone</span>
          <select
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className="rounded-xl bg-[var(--color-secondary-bg)] px-3 py-2 text-[var(--color-fg)]"
          >
            {commonTimezones.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-sm font-medium">Reminder time</span>
          <input
            type="time"
            value={notificationTime}
            onChange={(e) => setNotificationTime(e.target.value)}
            className="rounded-xl bg-[var(--color-secondary-bg)] px-3 py-2 text-[var(--color-fg)]"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-sm font-medium">Reminder interval ({reminderInterval} min)</span>
          <input
            type="range"
            min={15}
            max={240}
            step={15}
            value={reminderInterval}
            onChange={(e) => setReminderInterval(Number(e.target.value))}
          />
        </label>

        <label className="flex items-center justify-between rounded-xl bg-[var(--color-secondary-bg)] px-4 py-3">
          <span className="text-sm font-medium">Notifications</span>
          <input
            type="checkbox"
            checked={notificationsEnabled}
            onChange={(e) => setNotificationsEnabled(e.target.checked)}
            className="h-5 w-5"
          />
        </label>

        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="mt-2 w-full rounded-xl bg-[var(--color-button)] py-3 font-semibold text-[var(--color-button-fg)] disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </PageShell>
  );
}
